import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Download, Eye, FileText, User } from 'lucide-react';
import api, { assetUrl } from '../../services/api';

export default function StudentSubjectDetail() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [resources, setResources] = useState([]);
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const emptyResourceMessage = useMemo(() => {
    const messages = [
      {
        title: 'No headache today',
        line1: 'This subject has no resources yet, so your backpack and brain are both safe for now.',
        line2: 'Need notes? Contact your admin and gently remind them to drop the resources here.',
        badge: 'Status: calm before the study storm'
      },
      {
        title: 'Plot twist: zero PDFs',
        line1: 'No files have landed here yet. Your stress level can stay on airplane mode.',
        line2: 'Ping the admin if you want this page to transform into a real study zone.',
        badge: 'Status: waiting for academic thunder'
      },
      {
        title: 'Silent library mode',
        line1: 'Resources have not been allocated yet, so this page is currently doing meditation.',
        line2: 'If you need materials, contact admin and request the knowledge package.',
        badge: 'Status: peaceful, but temporary'
      },
      {
        title: 'Books on vacation',
        line1: 'No resources are here yet. The files are probably on a beach somewhere.',
        line2: 'Ask admin to bring them back before exam season starts sprinting.',
        badge: 'Status: sunscreen mode enabled'
      },
      {
        title: 'Knowledge loading... 0%',
        line1: 'This subject is still buffering. Not a single file has arrived.',
        line2: 'If you need content, contact admin and request a fast upload.',
        badge: 'Status: waiting for Wi-Fi and wisdom'
      },
      {
        title: 'Academic ghost town',
        line1: 'You found the quietest page in the system. No resources yet.',
        line2: 'Ping admin to populate this place with notes, slides, and hope.',
        badge: 'Status: tumbleweed detected'
      },
      {
        title: 'No PDF, no panic',
        line1: 'Relax. There are currently zero files trying to test your patience.',
        line2: 'Need them? Drop a polite reminder to admin.',
        badge: 'Status: stress-free zone'
      },
      {
        title: 'Empty shelf energy',
        line1: 'The resource shelf is clean and empty right now.',
        line2: 'Contact admin if you want this shelf to become useful.',
        badge: 'Status: minimalism, but accidental'
      },
      {
        title: 'Lecture notes went stealth',
        line1: 'No resources are visible. They are hiding better than exam questions.',
        line2: 'Ask admin to unhide them for the rest of us mortals.',
        badge: 'Status: ninja files missing'
      },
      {
        title: 'Homework dodge mode',
        line1: 'No study files yet. Today your backpack remains lightweight.',
        line2: 'Contact admin when you are ready to carry the academic weight.',
        badge: 'Status: peace before assignments'
      },
      {
        title: 'Brain break approved',
        line1: 'No resources have been uploaded for this subject yet.',
        line2: 'Need a challenge? Ask admin to publish the materials.',
        badge: 'Status: nap-friendly curriculum'
      },
      {
        title: 'The folder is shy',
        line1: 'This page has no files right now. It is being mysterious.',
        line2: 'Send admin a gentle nudge and watch the magic happen.',
        badge: 'Status: introvert folder'
      },
      {
        title: 'Study pack pending',
        line1: 'No resources are allocated here yet, so your schedule is still calm.',
        line2: 'Contact admin to unlock the official study starter kit.',
        badge: 'Status: pending dispatch'
      },
      {
        title: 'Calm before the quiz',
        line1: 'Nothing uploaded yet. Your stress graph can stay flat for now.',
        line2: 'If you need notes, tell admin this page is too peaceful.',
        badge: 'Status: quiet timeline'
      },
      {
        title: 'No files, just vibes',
        line1: 'Resources have not reached this destination yet.',
        line2: 'Contact admin to convert vibes into actual documents.',
        badge: 'Status: aesthetic but empty'
      },
      {
        title: 'Academic treasure hunt',
        line1: 'Treasure chest is empty. No resources found.',
        line2: 'Ask admin for a map, or at least a PDF.',
        badge: 'Status: X marks no spot'
      },
      {
        title: 'Document diet',
        line1: 'This subject is currently low-calorie: zero files.',
        line2: 'Contact admin to add some heavy study nutrients.',
        badge: 'Status: lean syllabus'
      },
      {
        title: 'Paperless by accident',
        line1: 'No resources here yet. Eco-friendly, but not intentionally.',
        line2: 'Ask admin to upload notes before your memory starts buffering.',
        badge: 'Status: trees are safe'
      },
      {
        title: 'Mission: find notes',
        line1: 'Current mission status: no resources available in this zone.',
        line2: 'Call admin HQ to deploy study files immediately.',
        badge: 'Status: awaiting command'
      },
      {
        title: 'The calm semester arc',
        line1: 'This subject has no files yet, so enjoy the temporary serenity.',
        line2: 'If you need materials now, contact admin for a quick drop.',
        badge: 'Status: episode 1, no chaos yet'
      },
      {
        title: 'Zero-byte drama',
        line1: 'No resources uploaded. Your storage and your soul are both free.',
        line2: 'Ping admin to begin the educational plot twist.',
        badge: 'Status: suspense mode'
      }
    ];

    return messages[Math.floor(Math.random() * messages.length)];
  }, [subjectId]);

  useEffect(() => {
    loadSubjectData();
  }, [subjectId]);

  const loadSubjectData = async () => {
    try {
      setLoading(true);
      setError('');

      const [resourceResult, subjectsResult] = await Promise.allSettled([
        api.get(`/student/subjects/${subjectId}/resources`),
        api.get('/student/subjects')
      ]);

      let resolvedSubject = null;
      let resolvedResources = [];
      let resolvedLecturers = [];

      if (resourceResult.status === 'fulfilled') {
        const responseData = resourceResult.value?.data || {};
        const payload = responseData.data || responseData;

        resolvedSubject = payload.subject || null;
        resolvedResources = payload.resources || [];
        resolvedLecturers = payload.lecturers || [];
      }

      // Backward compatibility with older backend shape where subject is not returned.
      if (!resolvedSubject && subjectsResult.status === 'fulfilled') {
        const subjectData = subjectsResult.value?.data || {};
        const subjectPayload = subjectData.data || subjectData;
        const grades = Array.isArray(subjectPayload.grades) ? subjectPayload.grades : [];
        const allSubjects = grades.flatMap((g) => g.subjects || []);
        resolvedSubject = allSubjects.find((s) => Number(s.subject_id) === Number(subjectId)) || null;
      }

      // Keep detail view usable even when older endpoint omits subject metadata.
      if (!resolvedSubject && resolvedResources.length > 0) {
        resolvedSubject = {
          subject_id: Number(subjectId),
          subject_name: `Subject ${String(subjectId).padStart(3, '0')}`,
          subject_code: `SUB-${String(subjectId).padStart(3, '0')}`
        };
      }

      setSubject(resolvedSubject);
      setResources(resolvedResources);
      setLecturers(resolvedLecturers);

      if (resourceResult.status === 'rejected') {
        throw resourceResult.reason;
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load subject details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (filePath, fileName) => {
    const downloadUrl = assetUrl(filePath);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (filePath) => {
    window.open(assetUrl(filePath), '_blank');
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('doc')) return '📝';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('video')) return '🎥';
    return '📎';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !subject) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/student/subjects')}
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Subjects
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/student/subjects')}
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Subjects
        </button>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">Subject not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/student/subjects')}
        className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Subjects
      </button>

      <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-gradient-to-r from-slate-100/90 via-white/80 to-blue-100/80 p-6 shadow-sm backdrop-blur-md">
        <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-sky-200/30 blur-2xl"></div>
        <div className="absolute -bottom-20 -left-16 h-44 w-44 rounded-full bg-indigo-200/30 blur-2xl"></div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/50 px-3 py-1 text-xs font-medium text-slate-600">
            <BookOpen className="h-3.5 w-3.5" /> Subject Detail
          </div>
          <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-slate-800">{subject.subject_name}</h1>
          <p className="mt-1 text-sm font-medium text-slate-600">{subject.subject_code || `SUB-${String(subject.subject_id).padStart(3, '0')}`}</p>
          {subject.description && <p className="mt-3 max-w-3xl text-sm sm:text-base text-slate-600">{subject.description}</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Subject Resources</h2>
        </div>

        {resources.length > 0 ? (
          <div className="space-y-3">
            {resources.map((resource) => (
              <div key={resource.resource_id} className="bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl flex-shrink-0">{getFileIcon(resource.file_type)}</span>
                      <div className="min-w-0">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-800 truncate">{resource.resource_heading}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{resource.file_name}</p>
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          Uploaded {new Date(resource.created_at).toLocaleDateString()}
                          {resource.uploaded_by_name && ` by ${resource.uploaded_by_name}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleView(resource.file_path)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                      title="View"
                    >
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      onClick={() => handleDownload(resource.file_path, resource.file_name)}
                      className="p-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition"
                      title="Download"
                    >
                      <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 via-rose-50 to-orange-50 p-6 sm:p-8 text-center">
            <div className="absolute -top-10 -right-8 h-24 w-24 rounded-full bg-orange-200/40 blur-2xl"></div>
            <div className="absolute -bottom-10 -left-8 h-24 w-24 rounded-full bg-rose-200/40 blur-2xl"></div>
            <div className="relative">
              <FileText className="w-11 h-11 mx-auto mb-3 text-amber-500" />
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">{emptyResourceMessage.title}</h3>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                {emptyResourceMessage.line1}
              </p>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                {emptyResourceMessage.line2}
              </p>
              <div className="mt-4 inline-flex items-center rounded-full border border-amber-200 bg-white/80 px-4 py-1.5 text-xs sm:text-sm font-medium text-amber-700 shadow-sm">
                {emptyResourceMessage.badge}
              </div>
            </div>
          </div>
        )}
      </div>

      {lecturers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Lecturers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {lecturers.map((lecturer) => (
              <div key={lecturer.user_id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{lecturer.full_name}</p>
                    <p className="text-xs text-gray-500">{lecturer.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}